import {useLocale} from '@aboutyou/i18n';
import {lazy, useHydrationDone} from '@aboutyou/lazy-imports';
import {ErrorBoundary, LoadingPageSpinner} from '@aboutyou/ui-components';
import type {LoggedInUserDataWithToken} from '@aboutyou/user';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {InfoBanner} from '../../../client-services/Login/InfoBanner/InfoBanner';
import {useSavedUser} from '../AuthenticationModal/hooks/useSavedUser';
import {useAuthenticationOpenState} from '../hooks/useAuthenticationOpenState';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';
import {useLoginScreenLogger} from '../hooks/useLoginScreenLogger';
import {AuthMethod} from '../types';
import {getShowLoginAsDefault} from '../utility/getShowLoginAsDefault';
import {AuthenticationContexts} from './AuthenticationContexts';
import {AuthenticationContext, AuthenticationProps} from './ctx';

const AuthenticationModalLazy = lazy(
  () =>
    import('../AuthenticationModal/AuthenticationModal').then(mod => ({
      default: mod.AuthenticationModal,
    })),
  {fallback: <LoadingPageSpinner />},
);

const SSOUrlCallbackHandlerLazy = lazy(() =>
  import(
    '../AuthenticationModal/components/SSOButtonsFetcher/SSOUrlCallbackHandler'
  ).then(mod => ({
    default: mod.SSOUrlCallbackHandler,
  })),
);

const AuthenticationValidatorLazy = lazy(() =>
  import('../AuthenticationValidator/AuthenticationValidator').then(mod => ({
    default: mod.AuthenticationValidator,
  })),
);

const isAuthMethod = (parameter: any): parameter is AuthMethod =>
  parameter === 'register' || parameter === 'login';

export const AuthenticationProvider = ({
  children,
  onSyncLegacy,
}: PropsWithChildren<{onSyncLegacy?: VoidFunction}>) => {
  const loginContext = useRef<AuthenticationContexts | null>(null);
  const isHydrationDone = useHydrationDone();
  const onCancelRef = useRef<VoidFunction>(undefined);
  const onSuccessRef =
    useRef<(customer: LoggedInUserDataWithToken) => void>(undefined);
  const onErrorRef = useRef<(e: string) => void>(undefined);
  const customBannerComponentRef = useRef<React.ReactNode>(undefined);
  const aboveHeaderBannerComponentRef = useRef<React.ReactNode>(undefined);
  const disableSuccessToastRef = useRef<boolean>(false);
  const forceRedirectToCheckoutOnAppleLoginSuccessRef = useRef<boolean>(false);

  const {isAuthenticated, isAuthenticatedCallback, isUserDataLoading} =
    useIsAuthenticated();
  const {isOpen, cancel, open, parameter, hide} = useAuthenticationOpenState({
    // We only want to enable the auto-open of the modal if the user is not authenticated
    enableParams: !isAuthenticated && !isUserDataLoading,
  });
  const {hasSavedUser, ...savedUser} = useSavedUser();
  const locale = useLocale();

  const [tabOverride, setTabOverride] = React.useState<AuthMethod | null>(null);
  const authMethod: AuthMethod =
    tabOverride ?? (isAuthMethod(parameter) ? parameter : 'login');

  useEffect(() => {
    if (!isOpen && tabOverride !== null) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTabOverride(null);
    }
  }, [isOpen, tabOverride]);

  const loginScreenLogger = useLoginScreenLogger();

  const onCancel = () => {
    cancel();
    onCancelRef.current?.();

    loginScreenLogger({
      event: 'cancelled',
      context: loginContext.current!,
    });
  };

  const onSuccess = (customer: LoggedInUserDataWithToken) => {
    hide();
    onSuccessRef.current?.(customer);
    onSyncLegacy?.();

    loginScreenLogger({
      event: 'login-success',
      context: loginContext.current!,
    });
  };

  const onError = (errorDetail: string) => {
    onErrorRef.current?.(errorDetail);

    loginScreenLogger({
      event: 'login-fail',
      context: loginContext.current!,
    });
  };

  const showUserAuthenticationPrompt = useCallback(
    async ({
      context,
      onCancel,
      onError,
      onSuccess,
      replaceHistory,
      customBannerComponent,
      aboveHeaderBannerComponent,
      disableSuccessToast,
      forceRedirectToCheckoutOnSSOLoginSuccess = false,
      forceSessionRefresh,
      needsCheckoutSession,
    }: AuthenticationProps) => {
      const {isAuthenticated, reason, user} = await isAuthenticatedCallback({
        needsCheckoutSession,
      });
      // Do not open the modal if the user is already authenticated, when we call the prompt to open
      // If already authenticated, trigger the onSuccess callback if there is one
      if (isAuthenticated && !forceSessionRefresh) {
        onSuccess?.({
          customerData: undefined,
          authToken: user?.token ?? '',
        });
        return;
      }

      loginContext.current = context;
      onCancelRef.current = onCancel;
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;
      customBannerComponentRef.current = customBannerComponent;
      aboveHeaderBannerComponentRef.current = aboveHeaderBannerComponent;
      disableSuccessToastRef.current = Boolean(disableSuccessToast);
      forceRedirectToCheckoutOnAppleLoginSuccessRef.current =
        forceRedirectToCheckoutOnSSOLoginSuccess;
      const shouldShowLoginAsDefault = getShowLoginAsDefault(locale);
      const resumeValidationFailed =
        isAuthenticated === false && reason === 'resume-validation';

      const parameter =
        forceSessionRefresh ||
        resumeValidationFailed ||
        shouldShowLoginAsDefault
          ? undefined
          : 'register';

      open({
        replaceHistory,
        parameter,
      });

      loginScreenLogger({
        event: 'opened',
        context,
      });
    },
    [isAuthenticatedCallback, locale, loginScreenLogger, open],
  );

  const onAuthenticationMethodChange = (type: AuthMethod) => {
    setTabOverride(type);
  };

  const value = useMemo(
    () => ({showUserAuthenticationPrompt}),
    [showUserAuthenticationPrompt],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
      {isHydrationDone && (
        <ErrorBoundary
          renderContext={
            isOpen ? 'authentication_modal' : 'authentication_modal_preload'
          }
          ErrorComponent={null}
        >
          <AuthenticationModalLazy
            onError={onError}
            onSuccess={onSuccess}
            onCancel={onCancel}
            onClickTab={onAuthenticationMethodChange}
            activeTab={authMethod}
            lastSeenUser={hasSavedUser ? savedUser : undefined}
            bannerComponent={customBannerComponentRef.current ?? <InfoBanner />}
            aboveHeaderBannerComponent={aboveHeaderBannerComponentRef.current}
            isOpen={isOpen}
            disableSuccessToast={disableSuccessToastRef.current}
            forceRedirectToCheckoutOnSSOLoginSuccess={
              forceRedirectToCheckoutOnAppleLoginSuccessRef.current
            }
          />
        </ErrorBoundary>
      )}
      {isAuthenticated && (
        <ErrorBoundary
          renderContext="authentication_validator"
          ErrorComponent={null}
        >
          <AuthenticationValidatorLazy />
        </ErrorBoundary>
      )}
      {isHydrationDone && (
        <ErrorBoundary
          renderContext="sso_url_callback_handler"
          ErrorComponent={null}
        >
          <SSOUrlCallbackHandlerLazy
            isSignUp={authMethod === 'register'}
            onError={onError}
            onSuccess={onSuccess}
            disableSuccessToast={false}
          />
        </ErrorBoundary>
      )}
    </AuthenticationContext.Provider>
  );
};
